import {
  ChevronLeft,
  CloudOff,
  LogOut,
  Menu as MenuIcon,
  Monitor,
  Moon,
  Search,
  Sun,
  User as UserIcon,
  X,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { authApi } from '../lib/api.js';
import { cx } from '../lib/cx.js';
import { useHotkeys } from '../lib/hotkeys.js';
import { useLiveStatus } from '../lib/live.js';
import { roleAtLeast, useSession } from '../lib/session.js';
import { setThemePreference, useThemePreference } from '../lib/theme.js';
import { CommandPalette } from './CommandPalette.js';
import { NAV_ITEMS, NAV_SECTIONS, SIDEBAR_STORAGE_KEY } from './nav.js';
import { VelocityLogo, VelocityMark } from './VelocityLogo.js';
import { Avatar, Dot } from './ui/Badge.js';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from './ui/Menu.js';

const ENVIRONMENT = (import.meta.env.VITE_ENVIRONMENT ?? '').trim();

/**
 * How long the feed may be down before the header says so out loud. Socket.IO
 * retries from one second (`lib/live.ts`), so a screen lock, a lift or a cell
 * handover heals well inside this window - and an indicator that shouts through
 * every one of those is one nobody reads by the time it matters.
 */
const OFFLINE_GRACE_MS = 5_000;

type FeedState = 'pending' | 'live' | 'down';

/**
 * `useLiveStatus`'s boolean, settled into the three things worth showing.
 *
 * The grace period delays the ALARM, never the truth. The moment the socket
 * drops, the indicator stops claiming "Live" and falls back to `pending`,
 * which asserts nothing - continuing to show a green dot for five seconds
 * about a connection the app already knows is gone would be worse than the bug
 * this replaces. Only the escalation to `down` waits, because Socket.IO
 * retries from one second and an indicator that shouts through every screen
 * lock is one nobody reads by the time it matters.
 */
function useSettledFeedState(connected: boolean): FeedState {
  const [state, setState] = useState<FeedState>(connected ? 'live' : 'pending');
  useEffect(() => {
    if (connected) {
      setState('live');
      return;
    }
    setState('pending');
    const timer = setTimeout(() => setState('down'), OFFLINE_GRACE_MS);
    return () => clearTimeout(timer);
  }, [connected]);
  return state;
}

/** What the status region announces. `pending` deliberately says nothing. */
const FEED_PHRASE: Record<FeedState, string> = {
  pending: '',
  live: 'Live feed connected',
  down: 'Live feed disconnected: alarms are not updating',
};

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Application chrome: collapsible left navigation, top bar with the command
 * palette, environment badge, live indicator, theme switch and user menu.
 * On a phone the navigation becomes a bottom bar.
 */
export function AppShell() {
  const navigate = useNavigate();
  const client = useQueryClient();
  const session = useSession();
  const role = session.data?.role;
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const feed = useSettledFeedState(useLiveStatus());
  const theme = useThemePreference();

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      /* preference simply does not persist */
    }
  }, [collapsed]);

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  useHotkeys(
    {
      'mod+k': (event) => {
        event.preventDefault();
        openPalette();
      },
    },
    true,
  );

  const visibleSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.minRole || roleAtLeast(role, item.minRole)),
  })).filter((section) => section.items.length > 0);

  const signOut = async (): Promise<void> => {
    try {
      await authApi.logout();
    } finally {
      // The cached principal would bounce the login page straight back into
      // the app, where every request 401s; clearing also drops per-user data.
      client.clear();
      void navigate('/login', { replace: true });
    }
  };

  return (
    <div className="flex h-full min-h-screen bg-[var(--canvas)] text-[var(--text)]">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-[80] focus:rounded-[var(--radius-sm)] focus:bg-[var(--surface)] focus:px-3 focus:py-2"
      >
        Skip to content
      </a>

      <aside
        className={cx(
          'fixed inset-y-0 left-0 z-40 flex flex-col border-r border-[var(--border)] bg-[var(--surface)]',
          'transition-[width,transform] duration-[var(--dur-base)] ease-[var(--ease)] lg:static lg:translate-x-0',
          collapsed ? 'lg:w-[56px]' : 'lg:w-[232px]',
          'w-[240px]',
          drawerOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-12 items-center gap-2 border-b border-[var(--border)] px-3">
          <NavLink
            to="/dashboard"
            className="flex min-w-0 items-center gap-2 font-semibold text-[var(--text)]"
            onClick={() => setDrawerOpen(false)}
          >
            {collapsed ? (
              <VelocityMark height={20} className="shrink-0" />
            ) : (
              <>
                <VelocityLogo height={18} showTrademark={false} className="shrink-0" />
                <span className="truncate text-[var(--text-2)]">Alerts</span>
              </>
            )}
          </NavLink>
          <button
            type="button"
            className="ml-auto hidden text-[var(--text-3)] hover:text-[var(--text)] lg:block"
            onClick={() => setCollapsed((v) => !v)}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          >
            <ChevronLeft
              className={cx(
                'h-4 w-4 transition-transform duration-[var(--dur-fast)]',
                collapsed && 'rotate-180',
              )}
            />
          </button>
          <button
            type="button"
            className="ml-auto text-[var(--text-3)] lg:hidden"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close navigation"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav aria-label="Primary navigation" className="flex-1 overflow-y-auto px-2 py-2">
          {visibleSections.map((section) => (
            <div key={section.title} className="mb-3">
              {!collapsed && (
                <p className="px-2 pb-1 text-[var(--text-xs)] font-semibold uppercase tracking-wide text-[var(--text-3)]">
                  {section.title}
                </p>
              )}
              <ul className="flex flex-col gap-0.5">
                {section.items.map(({ to, label, icon: Icon }) => (
                  <li key={to}>
                    <NavLink
                      to={to}
                      onClick={() => setDrawerOpen(false)}
                      title={collapsed ? label : undefined}
                      className={({ isActive }) =>
                        cx(
                          'flex items-center gap-2.5 rounded-[var(--radius-sm)] px-2 py-1.5 text-[var(--text-base)]',
                          'transition-colors duration-[var(--dur-fast)]',
                          isActive
                            ? 'bg-[var(--accent-soft)] font-medium text-[var(--accent-ink)]'
                            : 'text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]',
                        )
                      }
                    >
                      <Icon className="h-4 w-4 shrink-0" aria-hidden />
                      {!collapsed && <span className="truncate">{label}</span>}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      {drawerOpen && (
        <div
          className="fixed inset-0 z-30 lg:hidden"
          style={{ background: 'var(--scrim)' }}
          onClick={() => setDrawerOpen(false)}
          aria-hidden
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-12 items-center gap-2 border-b border-[var(--border)] bg-[var(--surface)] px-3">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation"
            className="text-[var(--text-2)] lg:hidden"
          >
            <MenuIcon className="h-5 w-5" />
          </button>

          <button
            type="button"
            onClick={openPalette}
            className={cx(
              'flex h-8 min-w-0 flex-1 items-center gap-2 rounded-[var(--radius-sm)] border border-[var(--border)]',
              'bg-[var(--surface-2)] px-2.5 text-[var(--text-base)] text-[var(--text-3)] hover:border-[var(--border-strong)]',
              'sm:max-w-[360px]',
            )}
          >
            <Search className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="truncate">Search or jump to…</span>
            <kbd className="ml-auto hidden shrink-0 rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface)] px-1 text-[var(--text-xs)] sm:block">
              ⌘K
            </kbd>
          </button>

          <div className="ml-auto flex items-center gap-2">
            {ENVIRONMENT && (
              <span
                className="hidden rounded-[var(--radius-pill)] border border-[var(--border-strong)] px-2 py-0.5 text-[var(--text-sm)] font-medium text-[var(--text-2)] sm:inline"
                title="Deployment environment"
              >
                {ENVIRONMENT}
              </span>
            )}
            {/*
              Live-feed status. One node at every width - it used to be
              `hidden sm:flex`, which is `display: none` on a phone, so the
              device the PWA exists for had no signal at all and the node was
              not even in the accessibility tree.

              The asymmetry is by STATE, not by breakpoint: healthy is a quiet
              dot and a word, down is a pill loud enough to catch an on-call
              eye. `role="status"` implies polite + atomic, so the phrase below
              is announced on every change and never interrupts.
            */}
            <span
              role="status"
              className={cx(
                'flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[var(--text-sm)]',
                feed === 'down'
                  ? 'rounded-[var(--radius-pill)] border border-[var(--danger)] px-2 py-0.5 font-medium'
                  : 'text-[var(--text-3)]',
              )}
              style={
                feed === 'down'
                  ? { backgroundColor: 'var(--danger-soft)', color: 'var(--danger)' }
                  : undefined
              }
            >
              {feed === 'down' ? (
                <CloudOff className="h-3.5 w-3.5 shrink-0" aria-hidden />
              ) : (
                <Dot color={feed === 'live' ? 'var(--success)' : 'var(--text-3)'} />
              )}
              {/*
                The word carries the state alongside the colour and the glyph,
                so none of the three is doing the job alone. It is aria-hidden
                because the region is atomic: without that, a change announces
                "Offline Live feed disconnected". The sr-only phrase is the
                region's whole accessible content, and it says what is offline
                rather than leaving a bare adjective in the header's reading
                order.

                `pending` renders no word and no phrase, so nothing is
                announced: the boolean cannot tell "reconnecting" from "never
                connected", and a grey dot is the only honest claim until it
                resolves.
              */}
              {feed !== 'pending' && (
                <span aria-hidden title={FEED_PHRASE[feed]}>
                  {feed === 'live' ? 'Live' : 'Offline'}
                </span>
              )}
              <span className="sr-only">{FEED_PHRASE[feed]}</span>
            </span>

            <Menu
              label="Appearance"
              align="end"
              triggerClassName="h-8 w-8 justify-center rounded-[var(--radius-sm)] text-[var(--text-2)] hover:bg-[var(--surface-2)]"
              trigger={
                theme === 'dark' ? (
                  <Moon className="h-4 w-4" />
                ) : theme === 'light' ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Monitor className="h-4 w-4" />
                )
              }
            >
              {(close) => (
                <>
                  <MenuLabel>Appearance</MenuLabel>
                  <MenuItem
                    icon={<Monitor className="h-3.5 w-3.5" />}
                    trailing={theme === 'system' ? '✓' : undefined}
                    onClick={() => {
                      setThemePreference('system');
                      close();
                    }}
                  >
                    Match system
                  </MenuItem>
                  <MenuItem
                    icon={<Sun className="h-3.5 w-3.5" />}
                    trailing={theme === 'light' ? '✓' : undefined}
                    onClick={() => {
                      setThemePreference('light');
                      close();
                    }}
                  >
                    Light
                  </MenuItem>
                  <MenuItem
                    icon={<Moon className="h-3.5 w-3.5" />}
                    trailing={theme === 'dark' ? '✓' : undefined}
                    onClick={() => {
                      setThemePreference('dark');
                      close();
                    }}
                  >
                    Dark
                  </MenuItem>
                </>
              )}
            </Menu>

            <Menu
              label="Account"
              align="end"
              triggerClassName="h-8 rounded-[var(--radius-sm)] px-1 hover:bg-[var(--surface-2)]"
              trigger={<Avatar name={session.data?.user?.display_name ?? session.data?.label} />}
            >
              {(close) => (
                <>
                  <MenuLabel>{session.data?.label ?? 'Not signed in'}</MenuLabel>
                  <p className="px-3 pb-1 text-[var(--text-sm)] text-[var(--text-3)]">
                    {session.data ? `Role: ${session.data.role}` : '—'}
                  </p>
                  <MenuSeparator />
                  <MenuItem
                    icon={<UserIcon className="h-3.5 w-3.5" />}
                    onClick={() => {
                      void navigate('/profile');
                      close();
                    }}
                  >
                    My profile
                  </MenuItem>
                  <MenuItem
                    icon={<LogOut className="h-3.5 w-3.5" />}
                    onClick={() => {
                      close();
                      void signOut();
                    }}
                  >
                    Sign out
                  </MenuItem>
                </>
              )}
            </Menu>
          </div>
        </header>

        <main id="main" className="min-w-0 flex-1 overflow-y-auto px-3 pb-20 pt-4 lg:px-6 lg:pb-8">
          <Outlet />
        </main>

        <nav
          aria-label="Primary"
          className="fixed bottom-0 left-0 right-0 z-20 flex border-t border-[var(--border)] bg-[var(--surface)] pb-[env(safe-area-inset-bottom)] lg:hidden"
        >
          {NAV_ITEMS.filter((item) => item.primary).map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cx(
                  'flex flex-1 flex-col items-center gap-0.5 py-2 text-[var(--text-xs)]',
                  isActive ? 'text-[var(--accent-ink)]' : 'text-[var(--text-3)]',
                )
              }
            >
              <Icon className="h-4 w-4" aria-hidden />
              {label}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={openPalette}
            className="flex flex-1 flex-col items-center gap-0.5 py-2 text-[var(--text-xs)] text-[var(--text-3)]"
          >
            <Search className="h-4 w-4" aria-hidden />
            Search
          </button>
        </nav>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
